import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { sendSupportEmail } from "../api/emailAPI";
import { useTheme } from "../context/ThemeContext";
import { Button, Card, Input, PageContainer, PageHeading, SectionHeading, Textarea } from "../components/ui";
import OnboardingWalkthrough, { OnboardingStep } from "../components/common/OnboardingWalkthrough";
import { useOnboarding } from "../hooks/useOnboarding";
import { safeGetItem } from "@mediatechgroup/app-version";
import { Link, useParams } from "react-router-dom";

/**
 * The club's own contact details, only the ones the club has filled in.
 *
 * Until 2026-10-11 a club that had not set them showed made-up ones
 * ("123 Athletics Way", "info@athleticsclub.com", "(123) 456-7890"), which
 * a parent could call or write to.
 */
export function clubContactDetails(settings: any): { address: string; email: string; phone: string } {
  const clean = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  return {
    address: clean(settings?.contact_address),
    email: clean(settings?.contact_email),
    phone: clean(settings?.contact_phone),
  };
}

interface PrivateContactPageProps {
  /**
   * True on the family route (/member/contact): the standard page frame and
   * H1. The public /:clubid/contact page renders this component inside its
   * own marketing frame and leaves it false, so its look does not change.
   */
  asPage?: boolean;
}

const PrivateContactPage = ({ asPage = false }: PrivateContactPageProps) => {
  const { isDarkMode } = useTheme();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const contact = clubContactDetails(settings);
  const hasContactDetails = !!(contact.address || contact.email || contact.phone);
  const { clubid } = useParams();
  // HELPER
  safeGetItem("authToken");
  const storedSettings = safeGetItem("settings");
  const storedClubName = safeGetItem("clubName");
  const userDetails = JSON.parse(safeGetItem("userDetails") || "{}");

  // Onboarding walkthrough steps
  const onboardingSteps: OnboardingStep[] = [
    {
      target: '[data-onboarding="page-title"]',
      title: 'Welcome to Contact',
      content: 'Get in touch with us for any questions or inquiries. View our contact information and send us a message directly through the form.',
      placement: 'bottom',
      highlightPadding: 12,
    },
    {
      target: '[data-onboarding="contact-info"]',
      title: 'Contact Information',
      content: 'Find our address, phone number, and email address. Click the phone or email to contact us directly.',
      placement: 'bottom',
      highlightPadding: 8,
    },
    {
      target: '[data-onboarding="contact-form"]',
      title: 'Contact Form',
      content: 'Fill out this form to send us a message. Enter your name, email, and message, then click "Send Message".',
      placement: 'left',
      highlightPadding: 8,
    },
  ];

  const {
    shouldShow,
    isActive,
    markAsCompleted,
    markAsDismissed,
    startWalkthrough,
  } = useOnboarding({
    storageKey: 'onboarding-contact',
    autoStart: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error("Please fill in all fields.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name,
        email,
        message,
        clubName: storedClubName,
      };
      const res = await sendSupportEmail(payload);

      if (res?.success) {
        toast.success("Message sent successfully!");
        setName("");
        setEmail("");
        setMessage("");
      } else {
        toast.error(res?.error || "Failed to send message.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storedSettings) {
      setSettings(JSON.parse(storedSettings));
    }
  }, [storedSettings]);

  // Autofill user details when component mounts
  useEffect(() => {
    if (userDetails?.email) {
      setEmail(userDetails.email);
    }
    if (userDetails?.first_name || userDetails?.last_name) {
      const fullName = `${userDetails.first_name || ''} ${userDetails.last_name || ''}`.trim();
      if (fullName) {
        setName(fullName);
      }
    }
  }, []);

  // Start onboarding after component mounts
  useEffect(() => {
    if (shouldShow && !isActive) {
      const timer = setTimeout(() => {
        startWalkthrough();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldShow, isActive, startWalkthrough]);

  const inputSize = asPage ? "md16" : "md";

  const panel = (
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left: Contact Info */}
          <div
            data-onboarding="contact-info"
            className="text-white p-8 flex flex-col justify-center"
            style={{ backgroundColor: isDarkMode ? "#4b5563" : "#6b7280" }}
          >
            {!asPage && (
              <h2 data-onboarding="page-title" className="text-3xl font-semibold mb-4">Get in Touch</h2>
            )}
            <p className="mb-4">
              We’d love to hear from you! Reach out with any questions or
              inquiries.
            </p>
            <div className="space-y-2">
              {contact.address && (
                <p className="whitespace-pre-line">
                  📍 <span className="font-semibold">Address:</span> {contact.address}
                </p>
              )}
              {contact.phone && (
                <p>
                  📞 <span className="font-semibold">Phone:</span>{" "}
                  <a href={`tel:${contact.phone}`} className="underline hover:text-orange-300">
                    {contact.phone}
                  </a>
                </p>
              )}
              {contact.email && (
                <p>
                  ✉️ <span className="font-semibold">Email:</span>{" "}
                  <a href={`mailto:${contact.email}`} className="underline hover:text-orange-300">
                    {contact.email}
                  </a>
                </p>
              )}
              {!hasContactDetails && (
                <p>Send the club a message with the form and they will get back to you.</p>
              )}
            </div>
            {clubid && (
              <p className="mt-6">
                Looking for how-to answers?{" "}
                <Link to={`/${clubid}/member/help`} className="underline font-semibold hover:text-orange-300">
                  Read the Help articles
                </Link>
                .
              </p>
            )}
          </div>

          {/* Right: Contact Form */}
          <div
            data-onboarding="contact-form"
            className={asPage ? "p-4 sm:p-6" : `p-8 ${isDarkMode ? 'bg-gray-800' : ''}`}
          >
            {asPage ? (
              <SectionHeading>Send a Message</SectionHeading>
            ) : (
              <h3 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-6`}>
                Send a Message
              </h3>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your Name"
                size={inputSize}
              />
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                size={inputSize}
              />
              <Textarea
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Your message..."
                size={inputSize}
                rows={5}
              />

              <Button
                type="submit"
                variant="primary"
                size={asPage ? "md" : "lg"}
                loading={loading}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
  );

  const walkthrough = shouldShow && (
    <OnboardingWalkthrough
      steps={onboardingSteps}
      storageKey="onboarding-contact"
      onComplete={markAsCompleted}
      onDismiss={markAsDismissed}
      isActive={isActive}
    />
  );

  if (asPage) {
    return (
      <PageContainer width="form">
        <PageHeading title="Get in Touch" />
        <Card padding="none" className="overflow-hidden">
          {panel}
        </Card>
        {walkthrough}
      </PageContainer>
    );
  }

  return (
    <div className="flex flex-col">
      <div className={`w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-md overflow-hidden`}>
        {panel}
      </div>

      {/* Onboarding Walkthrough */}
      {walkthrough}
    </div>
  );
};

export default PrivateContactPage;
